import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import Navbar from './components/Navbar';
import LoadingSpinner from './components/LoadingSpinner';
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import ProfilePage from './pages/ProfilePage';
import AuthCallback from './pages/AuthCallback';
import ExplorePage from './pages/ExplorePage';
import PreviouslyViewedPage from './pages/PreviouslyViewedPage';
import CommunityPage from './pages/CommunityPage';
import CommunityDetailPage from './pages/CommunityDetailPage';
import PostDetailPage from './pages/PostDetailPage';
import AdminFeedback from './pages/AdminFeedback';
import AdminPanel from './pages/AdminPanel';
import AdminReports from './pages/AdminReports';
import AdminStats from './pages/AdminStats';
import ManageAdminsPage from './components/Admin/ManageAdminPage';
import PublicProfilePage from './pages/PublicProfilePage';
import DocsPage from './pages/DocsPage';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// Public route wrapper
const PublicRoute = ({ children }: { children: React.ReactNode }) => {
  return <>{children}</>;
};

// Protected route wrapper
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const token = localStorage.getItem('token');
  
  if (!token) {
    return <Navigate to="/login" replace />;
  }
  
  return <>{children}</>;
};

const AppContent: React.FC = () => {
  return (
    <Router>
      <Toaster 
        position="top-right"
        toastOptions={{
          duration: 3000,
          style: {
            borderRadius: '10px',
            background: '#333',
            color: '#fff',
          },
        }}
        containerStyle={{
          top: 70, // Position below navbar
        }}
      />
      <Navbar />
      <Suspense fallback={<LoadingSpinner />}>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          
          {/* LOGIN ROUTES */}
          <Route path="/login" element={
            <PublicRoute>
              <LoginPage />
            </PublicRoute>
          } />
          
          {/* ADDED MISSING ROUTES FOR SIGNUP */}
          <Route path="/register" element={
            <PublicRoute>
              <LoginPage />
            </PublicRoute>
          } />
          <Route path="/signup" element={
            <PublicRoute>
              <LoginPage />
            </PublicRoute>
          } />

          <Route path="/profile" element={
            <ProtectedRoute>
              <ProfilePage />
            </ProtectedRoute>
          } />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/:username" element={<PublicProfilePage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
          <Route path="/explore" element={<ExplorePage />} />
          <Route path="/previously-viewed" element={<PreviouslyViewedPage />} />
          <Route path="/community" element={<CommunityPage />} />
          <Route path="/community/:slug" element={<CommunityDetailPage />} />
          <Route path="/post/:id" element={<PostDetailPage />} />
          <Route path="/admin/feedback" element={<AdminFeedback />} />
          <Route path="/admin" element={<AdminPanel />} />
          <Route path="/admin/reports" element={<AdminReports />} />
          <Route path="/admin/stats" element={<AdminStats />} />
          <Route path="/admin/manage-admins" element={<ManageAdminsPage />} />
          <Route path="/docs" element={<DocsPage />} />
        </Routes>
      </Suspense>
    </Router>
  );
};

const App: React.FC = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <AuthProvider>
          <AppContent />
        </AuthProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
};

export default App;